/* Org member / group list rows + bulk bar + row menu + modal helpers.
 * 1:1 port of the design prototype's .mrow / .bulkbar / .menu-pop / .plabel /
 * .toggle-row / .kv / .lo-card / .picktag classes (index.html). */

/* ── Member / group list (org-scoped, bp-style) ── */
.mrow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--color-grey-lightest);
}

.mrow:last-child {
  border-bottom: none;
}

.clk {
  cursor: pointer;
  transition: background 0.1s;
}

.clk:hover {
  background: var(--color-primary-lightest);
}

.sel {
  background: var(--color-primary-lighter);
}

.status {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.main {
  flex: 1;
  min-width: 0;
}

.name {
  font-size: var(--font-size-m);
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.sub {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.labels {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 6px;
}

/* ── List head: select-all + selection count (bp-frontend PersonsList .actions) ── */
.listHead {
  width: 100%;
  padding: var(--spacing-2) var(--spacing-4);
  border-bottom: 1px solid var(--color-grey-lighter);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.selectAll {
  width: fit-content;
}

.listHeadInfo {
  margin-left: auto;
  font-size: var(--font-size-s);
  color: var(--color-grey-light);
  user-select: none;
}

/* ── Members tab: filter bar above a separate list card (bp-frontend layout) ── */
.membersTab {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}
.membersToolbar {
  display: flex;
  flex-direction: column;
}

/* ── Toolbar inside a list card ── */
.listToolbar {
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-grey-lightest);
}

.listToolbarRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.listToolbarLeft {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Constrain the search + status inputs so the status Select doesn't stretch
   to full width (DS Select defaults to 100%). Keeps the toolbar on one row. */
.searchBox {
  width: 260px;
  max-width: 100%;
}
.filterBox {
  width: 190px;
  max-width: 100%;
}

.listToolbarRight {
  display: flex;
  align-items: center;
  gap: 12px;
}

.countMuted {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  font-variant-numeric: tabular-nums;
}

.eyebrow {
  font-size: var(--font-size-s);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-grey-light);
}

/* ── Toggle row (modals) ── */
.toggleRow {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 13px 14px;
  border: 1px solid var(--color-grey-lighter);
  border-radius: var(--border-radius, 8px);
  cursor: pointer;
}

.toggleRow input {
  width: 17px;
  height: 17px;
  margin-top: 1px;
  accent-color: var(--color-primary);
  flex-shrink: 0;
  cursor: pointer;
}

.toggleRowTitle {
  display: block;
  font-size: var(--font-size-m);
}

.toggleRowSub {
  display: block;
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 3px;
  line-height: 1.4;
}

/* ── KV list (Stammdaten) ── */
.kv {
  display: flex;
  flex-direction: column;
}

.kv > div {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 11px 0;
  border-bottom: 1px solid var(--color-grey-lightest);
  font-size: var(--font-size-m);
  align-items: baseline;
}

.kv > div:last-child {
  border-bottom: none;
}

.kv dt {
  color: var(--color-grey);
  flex-shrink: 0;
}

.kv dd {
  color: var(--color-text);
  text-align: right;
  word-break: break-word;
}

.codepill {
  background: var(--color-grey-lightest);
  padding: 2px 7px;
  border-radius: 5px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: var(--font-size-s);
}

/* ── Migration step list (Omni org) ── */
.steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.stepRow {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 10px 0;
  border-bottom: 1px solid var(--color-grey-lightest);
}

.stepRow:last-child {
  border-bottom: none;
}

.stepNum {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--color-grey-lightest);
  color: var(--color-grey);
  font-size: var(--font-size-m);
}

.stepLabel {
  font-size: var(--font-size-m);
}

/* generic modal field stack */
.stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modalScroll {
  max-height: 360px;
  overflow-y: auto;
  margin: 0 -4px;
}

.foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.footHint {
  font-size: var(--font-size-s);
  color: var(--color-grey-light);
}

.footActions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}
